import { useEffect, useState } from "react";
import { UserButton } from "@clerk/react";
import { Link } from "react-router-dom";
import { Timer, Share2, Bell } from "lucide-react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Logo } from "../components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useApi, errMsg, inr, fmtDate } from "../lib/api";
import { useT, LangSwitch } from "../lib/i18n";
import AdminDashboard, { DataTable, StatusPill, Code, Tx, Opts } from "./admin/Admin";

export function DashHeader({ me }) {
  const { tl, tc } = useT();
  return (
    <header className="bg-white border-b sticky top-0 z-40">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 h-14 flex items-center justify-between gap-2 sm:gap-3">
        <Logo compact />
        <div className="flex items-center gap-2 sm:gap-3 text-sm min-w-0">
          <span className="hidden md:inline text-slate-600" data-testid="dash-role">{me?.email} · <b>{me?.role && tc(me.role)}</b></span>
          <LangSwitch />
          <Link to="/" className="underline" data-testid="dash-website-link">{tl("Website")}</Link><UserButton />
        </div>
      </div>
    </header>
  );
}

function Countdown({ due }) {
  const { tl } = useT();
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  const ms = new Date(due) - now;
  if (ms <= 0) return <span className="pill bg-red-50 text-red-700 border-red-200">{tl("Window expired")}</span>;
  const txt = tl("{h}h {m}m left").replace("{h}", Math.floor(ms / 3600000)).replace("{m}", Math.floor((ms % 3600000) / 60000));
  return <span className="font-mono text-xs font-bold bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded" data-testid="lead-countdown">{txt}</span>;
}

const STATUSES = ["contacted", "survey_scheduled", "survey_done", "quote_sent", "quote_approved", "closed_lost"];
const BLANK = { type: "call", note: "", status: "", next_action_at: "", loss_reason: "" };

function LeadCard({ lead, api, reload }) {
  const { tl, tc } = useT();
  const [f, setF] = useState(BLANK);
  const a = lead.assignment;
  const save = async () => {
    try { await api.post(`/partner/leads/${lead.id}/activity`, { ...f, status: f.status || null, next_action_at: f.next_action_at ? new Date(f.next_action_at).toISOString() : null }); toast.success(tl("Activity recorded")); setF(BLANK); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-xl p-5" data-testid={`partner-lead-${lead.reference}`}>
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          {a.mode === "exclusive" ? <span className="badge-exclusive"><Timer className="h-3 w-3" />{tl("Exclusive")}</span> : <span className="badge-shared"><Share2 className="h-3 w-3" />{tl("Shared")}</span>}
          <span className="font-mono text-sm">{lead.reference}</span><StatusPill s={lead.status} />
        </div>
        {a.mode === "exclusive" && !a.responded_at && <Countdown due={a.response_due_at} />}
      </div>
      <p className="mt-3 font-semibold">{lead.name} · {lead.phone} · {lead.city}, {lead.state}</p>
      <p className="text-sm text-slate-600">{[lead.kind && tc(lead.kind), lead.requirement && tc(lead.requirement), lead.monthly_bill && `${tl("bill")} ₹${lead.monthly_bill}`, lead.business_name].filter(Boolean).join(" · ")}</p>
      <p className="text-xs text-slate-500">{tl("Last contact")} {fmtDate(lead.last_contact_at)} · {tl("Next follow-up")} {fmtDate(lead.next_follow_up_at)}{lead.appointment_at && ` · ${tl("Appointment")} ${fmtDate(lead.appointment_at)}`}</p>
      {lead.message && <p className="text-sm text-slate-700 mt-1">“{lead.message}”</p>}
      <div className="grid sm:grid-cols-4 gap-2 mt-4">
        <select className="border rounded-lg px-2 py-2 text-sm" aria-label={tl("Activity type")} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="activity-type"><Opts list={["call", "whatsapp", "visit", "note", "follow_up"]} /></select>
        <input className="border rounded-lg px-3 py-2 text-sm sm:col-span-2" aria-label={tl("What happened?")} placeholder={tl("What happened?")} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="activity-note" />
        <select className="border rounded-lg px-2 py-2 text-sm" aria-label={tl("Status")} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="activity-status"><option value="">{tl("Keep status")}</option><Opts list={STATUSES} /></select>
        <label className="text-xs sm:col-span-2">{tl("Next follow-up / visit time")}<input type="datetime-local" className="border rounded-lg px-3 py-2 text-sm w-full" value={f.next_action_at} onChange={(e) => setF({ ...f, next_action_at: e.target.value })} data-testid="activity-next" /></label>
        {f.status === "closed_lost" && <input className="border rounded-lg px-3 py-2 text-sm sm:col-span-2" placeholder={tl("Loss reason (required)")} value={f.loss_reason} onChange={(e) => setF({ ...f, loss_reason: e.target.value })} data-testid="activity-loss-reason" />}
      </div>
      <button onClick={save} className="mt-3 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-lg" data-testid="activity-save-btn">{tl("Record activity")}</button>
      {lead.activities?.length > 0 && <ul className="mt-4 text-xs text-slate-600 space-y-1 border-t pt-3">{lead.activities.map((x) => <li key={x.id}>{fmtDate(x.ts)} — {tc(x.type)}: {x.note}</li>)}</ul>}
    </div>
  );
}

const pct = (v) => `${(v * 100).toFixed(0)}%`;
const TICKET = { subject: "", category: "lead", message: "" };

function PartnerDashboard({ me }) {
  const api = useApi();
  const { tl, tc } = useT();
  const [leads, setLeads] = useState([]);
  const [com, setCom] = useState({ entries: [], statements: [] });
  const [tickets, setTickets] = useState([]);
  const [notes, setNotes] = useState([]);
  const [t, setT] = useState(TICKET);
  const load = () => {
    api.get("/partner/leads").then((r) => setLeads(r.data));
    api.get("/partner/commissions").then((r) => setCom(r.data));
    api.get("/partner/tickets").then((r) => setTickets(r.data));
    api.get("/partner/notifications").then((r) => setNotes(r.data));
  };
  useEffect(load, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = me.partner;
  const sendTicket = async () => {
    try { await api.post("/partner/tickets", t); toast.success(tl("Ticket created")); setT(TICKET); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-[1400px] mx-auto px-4 py-8" data-testid="partner-dashboard">
      <h1 className="text-2xl sm:text-3xl font-bold">{tl("Welcome")}, {p.name}</h1>
      <p className="text-sm text-slate-600 mt-1 font-mono" data-testid="partner-code">{p.partner_code} · {tc(p.tier)} · {tl("rate")} {pct(p.commission_rate)} · {tl("agreement")} {tc(p.agreement_status)} · {tl("training")} {tc(p.training_status)}</p>
      <Tabs defaultValue="leads" className="mt-6">
        <TabsList className="flex-wrap h-auto"><TabsTrigger value="leads" data-testid="ptab-leads">{tl("My leads")} ({leads.length})</TabsTrigger><TabsTrigger value="commissions" data-testid="ptab-commissions">{tl("Commission")}</TabsTrigger><TabsTrigger value="support" data-testid="ptab-support">{tl("Support")}</TabsTrigger><TabsTrigger value="alerts" data-testid="ptab-alerts"><Bell className="h-4 w-4 mr-1" />{tl("Alerts")}</TabsTrigger></TabsList>
        <TabsContent value="leads" className="grid gap-4 mt-4">{leads.length === 0 ? <p className="text-slate-600" data-testid="no-leads">{tl("No leads assigned to you right now.")}</p> : leads.map((l) => <LeadCard key={l.id} lead={l} api={api} reload={load} />)}</TabsContent>
        <TabsContent value="commissions" className="mt-4 space-y-4">
          <p className="text-sm bg-amber-50 border-l-4 border-amber-500 p-3">{tl("Commission lines are calculated on verified receipts only and are currently simulations, not approved or payable commission. Payout release is not active until final signed agreements are in place.")}</p>
          <DataTable testid="partner-commission-table" rows={com.entries} cols={[["lead_reference", "Project"], ["type", "Type", (v) => <Code v={v} />], ["basis", "ERCR", inr], ["rate", "Rate", pct], ["gross", "Gross", inr], ["tds", "TDS", inr], ["net", "Net", inr], ["status", "Status", (v) => <StatusPill s={v} />]]} />
        </TabsContent>
        <TabsContent value="support" className="mt-4 grid lg:grid-cols-2 gap-6">
          <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="ticket-form">
            <input className="w-full border rounded-lg px-3 py-2" aria-label={tl("Subject")} placeholder={tl("Subject")} value={t.subject} onChange={(e) => setT({ ...t, subject: e.target.value })} data-testid="ticket-subject" />
            <select className="w-full border rounded-lg px-3 py-2" aria-label={tl("Category")} value={t.category} onChange={(e) => setT({ ...t, category: e.target.value })} data-testid="ticket-category"><Opts list={["lead", "commission", "training", "materials", "technical", "other"]} /></select>
            <textarea className="w-full border rounded-lg px-3 py-2" rows={4} aria-label={tl("Describe the issue")} placeholder={tl("Describe the issue")} value={t.message} onChange={(e) => setT({ ...t, message: e.target.value })} data-testid="ticket-message" />
            <button onClick={sendTicket} className="bg-amber-700 text-white font-semibold px-4 py-2 rounded-lg" data-testid="ticket-submit">{tl("Create ticket")}</button>
          </div>
          <DataTable testid="partner-ticket-table" rows={tickets} cols={[["reference", "Ref"], ["subject", "Subject"], ["status", "Status", (v) => <StatusPill s={v} />], ["responses", "Replies", (v) => v?.map((r) => r.text).join(" | ")]]} />
        </TabsContent>
        <TabsContent value="alerts" className="mt-4"><DataTable testid="partner-alerts" rows={notes} cols={[["created_at", "When", fmtDate], ["title", "Title", (v) => <Tx v={v} />], ["body", "Details", (v) => <Tx v={v} />]]} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function Dashboard() {
  const api = useApi();
  const { tl } = useT();
  const [me, setMe] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get("/me").then((r) => setMe(r.data)).catch((e) => setErr(e)); }, [api]);
  return (
    <div className="min-h-screen bg-background">
      <Seo title={tl("Dashboard")} noindex />
      <DashHeader me={me} />
      {err && <p className="p-8 text-red-700" data-testid="dashboard-error">{errMsg(err)}</p>}
      {me?.role === "pending" && (
        <div className="max-w-xl mx-auto p-10 text-center" data-testid="pending-approval">
          <h1 className="text-2xl font-bold">{tl("Account awaiting approval")}</h1>
          <p className="text-slate-700 mt-3">{tl("Your login works, but access is granted only after an administrator approves your partner application or staff role. Use the same email address you applied with.")}</p>
          <Link to="/apply" className="inline-block mt-6 underline text-amber-800">{tl("Submit a partner application")}</Link>
        </div>
      )}
      {me?.role === "partner" && me.partner && <PartnerDashboard me={me} />}
      {me && ["admin", "finance", "operations", "content"].includes(me.role) && <AdminDashboard me={me} />}
    </div>
  );
}
